import { Template } from '@/store/slices/api/physicianInterpretationApiSlice';

import { DeviceVendors } from '@/pages/physician/analytics/constants';

import { Alert, ReportLocationEnum, ReportNote } from './alert';
import { Billing } from './billing';
import { IcdDiagnosisCode } from './icdDiagnosisCodes';
import { Appointment } from './matchDevice';
import { RegisteredNpi } from './npi';
import { Organization } from './organization';
import { Patient } from './patient';
import { Practitioner } from './reportsPatient';
import { ReportTemplate } from './reportTemplate';
import { User } from './user';
import {
  VendorDataOverrides,
  VtDevice,
  VtDeviceLead,
  VtDeviceMeasurement,
  VtMatchDevice,
  VtTransmission,
  VtVendor
} from './vendorTransmissionReport';

export enum SignatureStatus {
  Signed = 'SIGNED',
  NonPractitionerSigned = 'NON_PRACTITIONER_SIGNED',
  NotSigned = 'NOT_SIGNED',
  Dismissed = 'DISMISSED',
  SignatureNotRequired = 'SIGNATURE_NOT_REQUIRED',
  SentToProvider = 'SENT_TO_PROVIDER',
  PendingReview = 'PENDING_REVIEW'
}

export enum ReportType {
  InClinic = 'in-clinic',
  Manual = 'manual',
  RemoteInClinic = 'remote-in-clinic',
  Remote = 'remote'
}

export enum ReportColor {
  Red = 'RED',
  Yellow = 'YELLOW',
  Green = 'GREEN',
  Gray = 'GRAY',
  ReviewPending = 'REVIEW_PENDING'
}

export enum OrderStatus {
  EmptyAsPending = '',
  PendingOrder = 'Pending_Order',
  OrderFailedToPost = 'Order_Failed_To_Post',
  OrderRequiresSupport = 'Order_Requires_Support',
  ReadyToTransmit = 'Ready_To_Transmit',
  ReportFailedToPost = 'Report_Failed_To_Post',
  ReportRequiresSupport = 'Report_Requires_Support',
  SuccessfullyTransmitted = 'Successfully_Transmitted',
  OrderModified = 'Order_Modified'
}

export interface ReportUpdateFields {
  patient?: Partial<Patient>;
  notes?: Record<ReportLocationEnum, string>;
  overrides?: Partial<VendorDataOverrides>;
  programmedParameters?: Partial<ProgrammedParameters>;
  counters?: Partial<Counters>;
  report?: Partial<UpdateTransmissionReportParams>;
  inClinicEpisode?: Partial<ReportInClinicEpisode>;
}

export type ReportUpdateFieldKey = keyof ReportUpdateFields;

export interface AddToReportUpdateFieldsPayload<K extends ReportUpdateFieldKey> {
  key: K;
  value: ReportUpdateFields[K];
}

export interface TransmissionReportListPaginatedRequestDto {
  color?: ReportColor;
  signatureStatus?: SignatureStatus;
  clinicIds?: Array<string>;
  practitionerIds?: Array<string>;
  signingPractitionerIds?: Array<string>;
  deviceTypes?: Array<string>;
  vendors?: Array<string>;
  patientId?: string;
  deviceSerialNumber?: string;
  deviceModelNumber?: string;
  offset: number;
  limit: number;
  search?: string;
}

export interface MiniMenuReportsRequestDto {
  color?: ReportColor;
  signatureStatus?: SignatureStatus;
  clinicIds?: Array<string>;
  practitionerIds?: Array<string>;
  signingPractitionerIds?: Array<string>;
  deviceTypes?: Array<string>;
  search?: string;
  limit: number;
  offset?: number;
  vendors?: Array<string>;
  sort?: { column: string; order: 'ASC' | 'DESC' };
  isPendingReview?: boolean;
}

export interface NavigationReportIdsRequestDto {
  reportId: number;
  signatureStatus?: SignatureStatus;
  clinicIds?: Array<string>;
  practitionerIds?: Array<string>;
  signingPractitionerIds?: Array<string>;
  deviceTypes?: Array<string>;
  vendors?: Array<string>;
  search?: string;
  sort?: { column: string; order: 'ASC' | 'DESC' };
}
export interface ColorCount {
  color: ReportColor;
  count: number;
}

export interface ReviewPendingReportsResponse {
  data: MiniTransmissionReport[];
  count: number;
  total: number;
  colorCounts: ColorCount[];
}

export interface TransmissionReportGetDefaultIdRequestDto {
  color?: ReportColor;
  signatureStatus?: SignatureStatus;
  clinicIds?: Array<string>;
  practitionerIds?: Array<string>;
  signingPractitionerIds?: Array<string>;
  deviceTypes?: Array<string>;
  vendors?: Array<string>;
  patientId?: string;
  deviceSerialNumber?: string;
  deviceModelNumber?: string;
}

export interface MiniTransmissionReport {
  patient: {
    familyName: string;
    givenName: string;
    avatar?: string;
    mrn2?: string;
    practitioner?: {
      familyName: string;
      givenName: string;
      npi: string;
      id: string;
    };
    birthDate?: string;
  };
  billing: {
    billingDate?: string;
  };
  id: number;
  signingProvider: string;
  deviceType: string;
  modelNumber: string;
  modelName: string;
  vendor: string;
  type: string;
  category: string;
  color: string;
  content: string;
  transmissionDate: string;
  transmissionType: ReportType;
  /** @deprecated Use reportType === 'HEART_FAILURE' for logic. API returns for backward compat. */
  isHeartFailure?: boolean;
  reportType?: string;
  isAppDeterminedBillable?: boolean;
  isEriTracked?: boolean;
  nextScheduledTransmission?: string;
  lastBillableDate?: string;
  triage?: {
    autoTriagedColor: ReportColor | null;
    protocolExplanation: string | null;
    triagedAt: string | null;
  } | null;
}

export interface UnsignedReportCountsByColorRequest {
  clinicIds?: Array<string>;
  practitionerIds?: Array<string>;
  signingPractitionerIds?: Array<string>;
  deviceTypes?: Array<string>;
  vendors?: Array<string>;
  signatureStatus?: SignatureStatus;
  search?: string;
}

export type TransmissionReportCount = {
  [key in ReportColor]: number;
};

export interface UnsignedReportCountsByColorResponse {
  counts: TransmissionReportCount;
  clinicIds: string[];
}

export interface TransmissionCategoryResponse {
  data: MiniTransmissionReport[];
  count: number;
  total: number;
}

export interface TransmissionReportDetailsResponse {
  transmissionReport: TransmissionReport;
}

export interface TransmissionReport {
  transmission: VtTransmission;
  PDFUploadKey: string | null;
  nextScheduledTransmissionDate: Date | null;
  lastBillableDate: Date | null;
  isBillable: boolean;
  isEriTracked: boolean;
  transmissionDate: string;
  transmissionFileLocation: string | null;
  transmissionFileName: string | null;
  notes: Array<ReportNote>;
  alerts: Array<Alert>;
  signatures: Array<ReportSignature>;
  device?: VtDevice;
  matchDevice?: VtMatchDevice;
  deviceMeasurement?: VtDeviceMeasurement;
  deviceLeads?: Array<VtDeviceLead>;
  transmissionVendor?: VtVendor;
  deviceVendor?: VtVendor;
  overrides: any;
  counters: Counters;
  billing?: Billing;
  filesUploadStatus: TransmissionReportFilesUploadStatus | null;
  id: number;
  color: ReportColor;
  patient: Patient;
  signatureStatus: SignatureStatus;
  orderStatus: string | null;
  smartNarrativeStatus: string | null;
  comment: string | undefined;
  account: Organization;

  smartNarrativeStatusUpdated: string | null;
  transmissionType: string;
  /** @deprecated Use reportType === 'HEART_FAILURE' for logic. API returns for backward compat. */
  flagHeartFailure: boolean;

  isEdited: boolean | false;
  editedUser: string;
  editedDate: Date;
  activeTemplate: ReportTemplate | null;
  previousTxDeviceMeasurement?: VtDeviceMeasurement;
  previousTxDeviceLeads?: Array<VtDeviceLead>;
  programmedParameter?: ProgrammedParameters | null;
  followUpReason: string | null;
  otherReason: string | undefined;
  presentingCard?: ReportPresentingCard | null;
  inClinicEpisode?: ReportInClinicEpisode | null;
  programmingChanges: string | null;
  icdDiagnosis: IcdDiagnosisCode | null;
  signingProvider: Practitioner | null;
  emrEncounterId: string | null;
  physicianInterpretationTemplate: Template | null;
  reportType: ReportCategoryType;
  referringProvider: RegisteredNpi | null;
  referringPhysician: string | null;
  practitioner: Practitioner | null;
  inClinicPractitioner: Practitioner | null;
  underReviewByOtherUser?: { id: string; given: string; family: string } | null;
  appointment: Appointment | null;
  monitorReason: string;
  ocrStatus: string | null;
}

export enum ReportCategoryType {
  Routine = 'ROUTINE',
  ERI_Tracking = 'ERI_TRACKING',
  Heart_Failure = 'HEART_FAILURE',
  Combo = 'COMBO'
}

export interface ReportInClinicEpisode {
  id: string;
  atrModeSwitch?: string | null;
  maxTime?: string | null;
  atrialBurden?: string | null;
  vhrEpisodes?: string | null;
  episodeComments?: string | null;
}

export interface ReportPresentingCard {
  presentingRhythm: string | null;
  presentingRate: string | null;
  underlyingRhythm: string | null;
  underlyingRate: string | null;
}

export type OcrStatus = 'IN_PROGRESS' | 'PROCESSED' | 'FAILED' | 'UNKNOWN';

export interface OcrStatusResponse {
  status: OcrStatus;
}

export interface UpdateTransmissionReportParams {
  color: ReportColor;
  followUpReason: string | null;
  otherReason: string | undefined;
  presentingRhythm: string | null;
  presentingRate: string | null;
  underlyingRhythm: string | null;
  underlyingRate: string | null;
  practitionerId: string | null;
  comment: string | null;
  programmingChanges: string | null;
  reportType: ReportCategoryType;
  referringProvider: string | null;
  referringPhysician: string | null;
  practitioner: Practitioner | null;
  inClinicPractitioner: Practitioner | null;
  emrEncounterId: string | null;
}

export interface ReportSignature {
  id: string;
  user: User;
  createdAt: string;
  updatedAt: string;
  signDate: string;
  updatedBy: string;
}

export interface GetPatientReportsResponse {
  transmissionReports: {
    id: string;
    deviceType: string;
    createdAt: string;
    signatureStatus: SignatureStatus;
    transmissionType: string;
    transmissionAt: string;
  }[];
}

export interface TransmissionReportFilesUploadStatus {
  fileStatus: Array<FileUploadStatus>;
}

export interface FileUploadStatus {
  fileName: string;
  status: string;
}

export interface ProgrammedParameters {
  id: string;
  mode: string | null;
  lowerRate: string | null;
  upperRate: string | null;
  atOrAf: string | null;
  vtZone1: string | null;
  vtZone2: string | null;
  vf: string | null;
  transmissionId: number;
  pvab?: string | null;
  pvarp?: string | null;
}

export interface UpdateProgrammedParametersRequest {
  transmissionReportId: number;
  programmedParametersUpdateParams: Partial<ProgrammedParameters>;
}

export interface Counters {
  Id: string;
  symptomCounters: string | null;
  symptomTable: string | null;
  tachyCounters: string | null;
  tachyTime: string | null;
  pauseCounters: string | null;
  pauseTime: string | null;
  bradyCounters: string | null;
  bradyTime: string | null;
  ATCounters: string | null;
  ATTime: string | null;
  AFCounters: string | null;
  AFTime: string | null;
  TimeInATAFCounters: string | null;
  TimeInATAFTime: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface SignDateParams {
  timezone: string;
  templateId?: string;
  comment?: string;
}

export interface SignReportRequest {
  reportId: number;
  signDateParams: SignDateParams;
}

export interface SignReportResponse extends ReportSignature {
  transmissionReport: Partial<TransmissionReport>;
}

export interface SearchUnsignedReportsRequest {
  color?: ReportColor;
  query?: string;
  excludeGlobalFilters?: boolean;
  clinicIds?: Array<string>;
  practitioners?: Array<string>;
  signingPractitioners?: Array<string>;
  deviceTypes?: Array<string>;
}

export interface SearchUnsignedReportsResponse {
  transmissionReports: Array<UnsignedReportSearchResult>;
}

export interface UnsignedReportSearchResult {
  id: number;
  color: string;
  givenName: string;
  familyName: string;
  deviceType: string;
  transmissionDate: string;
  deviceSerialNumber: string;
  deviceModelNumber: string;
}

export const FOLLOW_UP_OPTIONS = [
  { value: 'post-implant', label: 'Post-implant' },
  { value: 'routine', label: 'Routine' },
  { value: 'emergent', label: 'Emergent' },
  { value: 'other', label: 'Other' }
];

export const ReportTypeTitleMap: Record<string, string> = {
  [ReportType.InClinic]: 'Manual In-Clinic',
  [ReportType.Manual]: 'Manual',
  [ReportType.RemoteInClinic]: 'Remote In-Clinic',
  [ReportType.Remote]: 'Remote'
};

export type CreateDeviceDto = {
  deviceId?: string;
  manufacturer?: DeviceVendors;
  type?: string;
  modelName?: string;
  modelNumber?: string;
  serialNumber?: string;
  implantDate?: string;
};

export type CreateAppointmentDto = {
  appointmentId?: string;
  appointmentName?: string;
  type?: string;
  scheduledDate: Date | null;
  status?: string;
  frequency?: string;
  frequencyNumber?: number;
  salesforceRemoteId?: string;
  appointmentNotes?: string;
};

export type CreateReportDto = {
  patientId?: string;
  transmissionType?: string;
  reportType?: string;
  dateOfService?: string | null;
  device?: CreateDeviceDto;
  files?: string[];
  color?: string;
};
export interface TransmissionReportDiagnosis {
  id: string;
  diagnosisCode: string;
  description: string;
  isPrimary: boolean;
}
export interface TransmissionReportDiagnosisListResponse {
  data: TransmissionReportDiagnosis[];
  limit: number;
  total: number;
  totalPages: number;
  page: number;
}

export interface GetTransmissionReportDiagnosesParams {
  reportId: number;
  isPrimary?: boolean;
  clinicalStatus?: number;
  verificationStatus?: number;
  limit?: number;
  page?: number;
}

export interface GetPatientTxReportsRequest {
  colors?: ReportColor[];
  reportTypes?: ReportCategoryType[];
  transmissionTypes?: ReportType[];
  signatureStatuses?: SignatureStatus[];
  isDismissed?: boolean;
  limit?: number;
  page?: number;
}

export interface MiniTxReport {
  id: number;
  createdAt: string;
  color: ReportColor;
  signatureStatus: SignatureStatus;
  transmissionType: ReportType;
  reportType: ReportCategoryType;
  isBillable: boolean;
  /** @deprecated Use reportType === 'HEART_FAILURE' for logic. API returns for backward compat. */
  flagHeartFailure: boolean;
  deviceType: string;
  dateOfService: string;
  transmissionDate: string;
}

export interface GetPatientTxReportsResponse {
  data: MiniTxReport[];
  total: number;
  totalPages: number;
  page: number;
  limit: number;
}
